import * as React from 'react'
import * as classNames from 'classnames'
import { Field, FieldProps, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { Checkbox, Input, Select } from '@mobiscroll/react-latest'
import { ILegalEntityObj, IOrgObj } from '../../../../types'
import UIMessages from '../../../../utils/ui-messages'
import { isNotBlank } from '../../../../utils/validators'
import ErrorMessage from '../../../ui/ErrorMessage'
import CreateOrganizationBottomBar from '../CreateOrganizationBottomBar'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import { organizationTypeOptions, stateOptions } from 'src/constants/create-organization-form'

export interface IFormValues {
  formError?: string
  addressLine1: string
  addressLine2: string
  agreedToTerms: boolean
  businessName: string
  businessTaxId: number
  city: string
  firstName: string
  lastName: string
  postalCode: string
  state: string
  type: string
  id?: string
  isLegalEntityCompleted?: boolean
}

interface ISelectOption {
  label: string
  value: string
}

interface IFormProps {
  isSaved: boolean
  isCompleted: boolean
  organization: IOrgObj
  orgId: string
  isEdit?: boolean
  legalEntity: ILegalEntityObj
  isLegalEntityCompleted?: boolean
}

interface ILegalEntityFormProps {
  currentStep: number
  isCompleted: boolean
  isEdit?: boolean
  isSaved: boolean
  legalEntity: ILegalEntityObj
  organization: IOrgObj
  orgId: string
  isLegalEntityCompleted?: boolean
  onCompleteStep: (step: number) => void
  onSaveLegalEntity: (legalEntity: ILegalEntityObj) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

export const InnerForm = (props: IFormProps & FormikProps<IFormValues>): any => {
  const {
    errors,
    isCompleted,
    isEdit,
    isLegalEntityCompleted,
    isSaved,
    isSubmitting,
    orgId,
    touched,
    values,
    setFieldTouched,
    setFieldValue
  } = props

  const inputProps = {
    inputStyle: 'box' as const,
    labelStyle: 'floating' as const,
    theme: 'ios'
  }

  const orgTypeDefaultValue =
    organizationTypeOptions.find(o => o.value === values.type) || organizationTypeOptions[0].value
  const stateDefaultValue = stateOptions.find(o => o.value === values.state) || stateOptions[0].value

  const textInput = ({ field }: FieldProps<IFormValues>) => {
    const inputDetails = {
      firstName: {
        label: `${UIMessages.common.firstName}*`,
        required: true,
        type: 'text'
      },
      lastName: {
        label: `${UIMessages.common.lastName}*`,
        required: true,
        type: 'text'
      },
      businessName: {
        label: `${UIMessages.organization.legalEntity.businessName}*`,
        required: true,
        type: 'text'
      },
      businessTaxId: {
        label: `${UIMessages.organization.legalEntity.businessTaxId}*`,
        required: true,
        type: 'text'
      },
      addressLine1: {
        label: `${UIMessages.organization.legalEntity.addressLine1}*`,
        required: true,
        type: 'text'
      },
      addressLine2: {
        label: `${UIMessages.organization.legalEntity.addressLine2}*`,
        required: true,
        type: 'text'
      },
      city: {
        label: `${UIMessages.organization.legalEntity.city}*`,
        required: true,
        type: 'text'
      },
      state: {
        label: `${UIMessages.organization.legalEntity.state}*`,
        required: true,
        type: 'text'
      },
      postalCode: {
        label: `${UIMessages.organization.legalEntity.postalCode}*`,
        required: true,
        type: 'text'
      }
    }
    const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Input {...field} {...inputProps} {...textInputProps} />
  }

  const select = (value: FieldProps<IFormValues>) => {
    const { field } = value
    const onChangeWrap = (value: string) => {
      setFieldTouched(field.name)
      setFieldValue(field.name, value)
    }

    const inputDetails = {
      type: {
        data: organizationTypeOptions,
        defaultValue: orgTypeDefaultValue,
        filter: false,
        label: `${UIMessages.organization.legalEntity.type}*`,
        required: true
      },
      state: {
        data: stateOptions,
        defaultValue: stateDefaultValue,
        filter: true,
        label: `${UIMessages.organization.legalEntity.state}*`,
        required: true
      }
    }
    const selectProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Select {...field} {...inputProps} {...selectProps} onChange={({ value }) => onChangeWrap(value)} />
  }

  const checkBoxInput = ({ field }: FieldProps<IFormValues>) => {
    const inputDetails = {
      agreedToTerms: {
        checked: values.agreedToTerms,
        defaultChecked: true,
        label: `${UIMessages.organization.legalEntity.iAgreeToThe} ${
          UIMessages.organization.legalEntity.termsAndConditions
        }`
      }
    }
    const { label, ...checkboxProps } = inputDetails[field.name as keyof typeof inputDetails]
    return (
      <div className='flex'>
        <Checkbox {...field} {...checkboxProps} />
        <label className='font-grey3'>{label}</label>
      </div>
    )
  }

  const bodyStyle = isEdit
    ? classNames({ 'pointer-events-auto opacity1': !isSubmitting }, { 'pointer-events-none opacity04': isSubmitting })
    : classNames(
        { 'pointer-events-auto opacity1': !(isSaved || isCompleted || isSubmitting) },
        { 'pointer-events-none opacity04': isSaved || isCompleted || isSubmitting }
      )

  const isTouched = Object.keys(touched).length > 0

  const nextStepPath = isEdit ? `/organizations/${orgId}/edit/bank-account` : '/organizations/create/bank-account'

  const prevStepPath = isEdit
    ? `/organizations/${orgId}/edit/organization-location`
    : '/organizations/create/organization-location'

  const disableContinueBtn = isEdit ? false : !isEdit && isLegalEntityCompleted ? false : true

  return (
    <Form className='org-legal-entity-container'>
      <FloatingForm>
        <div className={`${bodyStyle}`}>
          <ErrorMessage visible={!!errors.formError}>{UIMessages.common.failedRiderCreate}</ErrorMessage>
          <div className='mbsc-grid' style={{ padding: '0' }}>
            <div className='mbsc-row'>
              <div className='mbsc-col-lg-8'>
                <p className='bold font-grey3'>{UIMessages.common.legalEntity}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <Field name='type' render={select} />
                    {touched.type && errors.type && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.type}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='firstName' render={textInput} />
                    {touched.firstName && errors.firstName && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.firstName}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='lastName' render={textInput} />
                    {touched.lastName && errors.lastName && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.lastName}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='businessName' render={textInput} />
                    {touched.businessName && errors.businessName && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.businessName}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='businessTaxId' render={textInput} />
                    {touched.businessTaxId && errors.businessTaxId && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.businessTaxId}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='addressLine1' render={textInput} />
                    {touched.addressLine1 && errors.addressLine1 && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.addressLine1}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='addressLine2' render={textInput} />
                    {touched.addressLine2 && errors.addressLine2 && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.addressLine2}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='city' render={textInput} />
                    {touched.city && errors.city && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.city}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='state' render={select} />
                    {touched.state && errors.state && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.state}</span>
                    )}
                  </div>
                  <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                    <Field name='postalCode' render={textInput} />
                    {touched.postalCode && errors.postalCode && (
                      <span className='font-small weight-300 font-red block relative h0'>{errors.postalCode}</span>
                    )}
                  </div>
                  {!isEdit && (
                    <div className='mbsc-col-12 mbsc-padding'>
                      <Field name='agreedToTerms' render={checkBoxInput} />
                      {touched.agreedToTerms && errors.agreedToTerms && !values.agreedToTerms && (
                        <span className='font-small weight-300 font-red block relative h0 mt1'>
                          {errors.agreedToTerms}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className='mbsc-col-lg-4'>
                <p className='bold font-grey3'>{UIMessages.common.tips}</p>
                <div className='bb-shadow-grey' />
                <div className='mbsc-row'>
                  <div className='mbsc-col-12 mbsc-padding'>
                    <div className='font-light-grey'>{UIMessages.common.legalEntityTips}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FloatingForm>
      <CreateOrganizationBottomBar
        isEdit={isEdit}
        isSaved={!isTouched || (!isEdit && (isSaved || isCompleted))}
        isSaving={isSubmitting}
        previousStepPath={prevStepPath}
        nextStepPath={nextStepPath}
        disableContinueBtn={disableContinueBtn}
      />
    </Form>
  )
}

export const LegalEntityForm = withFormik<ILegalEntityFormProps, IFormValues>({
  enableReinitialize: true,
  mapPropsToValues: (props: ILegalEntityFormProps): IFormValues => {
    const {
      id,
      addressLine1,
      addressLine2,
      businessName,
      businessTaxId,
      city,
      firstName,
      lastName,
      postalCode,
      state,
      type
    } = props.legalEntity
    const { isLegalEntityCompleted } = props
    return {
      id,
      addressLine1,
      addressLine2,
      businessName,
      businessTaxId,
      city,
      firstName,
      isLegalEntityCompleted,
      lastName,
      postalCode,
      agreedToTerms: true,
      state: state ? state : stateOptions[0].value,
      type: type ? type : organizationTypeOptions[0].value
    }
  },
  mapValuesToPayload: x => x,
  validate: (
    {
      addressLine1,
      addressLine2,
      agreedToTerms,
      businessName,
      city,
      firstName,
      lastName,
      postalCode,
      state
    }: IFormValues,
    { showMessage, isEdit }
  ): FormikErrors<IFormValues> => {
    const errors: FormikErrors<IFormValues> = {}
    if (!isNotBlank(addressLine1)) {
      errors.addressLine1 = UIMessages.organization.validation.addressLine1IsMissing
    }
    if (!isNotBlank(addressLine2)) {
      errors.addressLine2 = UIMessages.organization.validation.addressLine2IsMissing
    }
    if (!isNotBlank(businessName)) {
      errors.businessName = UIMessages.organization.validation.businessNameIsMissing
    }
    if (!isNotBlank(city)) {
      errors.city = UIMessages.organization.validation.cityIsMissing
    }
    if (!isNotBlank(firstName)) {
      errors.firstName = UIMessages.organization.validation.firstNameIsMissing
    }
    if (!isNotBlank(lastName)) {
      errors.lastName = UIMessages.organization.validation.lastNameIsMissing
    }
    if (!isNotBlank(postalCode)) {
      errors.postalCode = UIMessages.organization.validation.postalCodeIsMissing
    }
    if (!isNotBlank(state)) {
      errors.state = UIMessages.organization.validation.stateIsMissing
    }
    if (!isEdit && !agreedToTerms) {
      errors.agreedToTerms = UIMessages.organization.legalEntity.agreedToTerms
    }
    return errors
  },
  handleSubmit: (
    {
      id,
      addressLine1,
      addressLine2,
      businessName,
      businessTaxId,
      city,
      firstName,
      lastName,
      postalCode,
      state,
      type
    }: IFormValues,
    { props, setSubmitting }
  ) => {
    setSubmitting(true)
    props.onCompleteStep(2)
    props
      .onSaveLegalEntity({
        id,
        addressLine1,
        addressLine2,
        businessName,
        businessTaxId,
        city,
        firstName,
        lastName,
        postalCode,
        state,
        type
      })
      .then(() => {
        setSubmitting(false)
      })
      .catch(error => console.log(error))
  }
})(InnerForm)

export default LegalEntityForm



// WEBPACK FOOTER //
// ./src/components/organizations/create/legalentity/LegalEntityForm.tsx